'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface CalculatorInputs {
  monthlyTickets: number;
  avgHandleTime: number;
  agentHourlyCost: number;
  currentSatisfaction: number;
}

interface CalculatorResults {
  monthlySavings: number;
  annualSavings: number;
  timeReduction: number;
  satisfactionIncrease: number;
  roi: number;
}

const ROICalculator = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [inputs, setInputs] = useState<CalculatorInputs>({
    monthlyTickets: 5000,
    avgHandleTime: 15,
    agentHourlyCost: 25,
    currentSatisfaction: 75
  });

  const [results, setResults] = useState<CalculatorResults>({
    monthlySavings: 0,
    annualSavings: 0,
    timeReduction: 0,
    satisfactionIncrease: 0,
    roi: 0
  });

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    if (!isHydrated) return;

    const automationRate = 0.70;
    const timeReductionRate = 0.85;
    const satisfactionBoost = 23;

    const currentMonthlyCost = (inputs.monthlyTickets * inputs.avgHandleTime / 60) * inputs.agentHourlyCost;
    const automatedTickets = inputs.monthlyTickets * automationRate;
    const timePerTicket = inputs.avgHandleTime * (1 - timeReductionRate);
    const newMonthlyCost = (automatedTickets * timePerTicket / 60) * inputs.agentHourlyCost;
    const monthlySavings = currentMonthlyCost - newMonthlyCost;
    const annualSavings = monthlySavings * 12;
    const implementationCost = 50000;
    const roi = ((annualSavings - implementationCost) / implementationCost) * 100;

    setResults({
      monthlySavings: Math.round(monthlySavings),
      annualSavings: Math.round(annualSavings),
      timeReduction: Math.round(timeReductionRate * 100),
      satisfactionIncrease: Math.min(98, inputs.currentSatisfaction + satisfactionBoost),
      roi: Math.round(roi)
    });
  }, [inputs, isHydrated]);

  const handleInputChange = (field: keyof CalculatorInputs, value: number) => {
    if (!isHydrated) return;
    setInputs(prev => ({ ...prev, [field]: value }));
  };

  if (!isHydrated) {
    return (
      <section className="py-20 lg:py-32 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-card rounded-2xl p-8 lg:p-12 border border-border shadow-lg">
            <div className="animate-pulse space-y-8">
              <div className="h-8 bg-muted rounded w-3/4 mx-auto" />
              <div className="h-4 bg-muted rounded w-1/2 mx-auto" />
              <div className="grid md:grid-cols-2 gap-8 mt-12">
                <div className="space-y-4">
                  <div className="h-6 bg-muted rounded w-1/2" />
                  <div className="h-12 bg-muted rounded" />
                </div>
                <div className="space-y-4">
                  <div className="h-6 bg-muted rounded w-1/2" />
                  <div className="h-12 bg-muted rounded" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 lg:py-32 bg-muted/30">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-card rounded-2xl p-8 lg:p-12 border border-border shadow-lg">
          {/* Header */}
          <div className="text-center max-w-3xl mx-auto mb-12">
            <div className="inline-flex items-center space-x-2 bg-accent/10 text-accent px-4 py-2 rounded-full mb-6">
              <Icon name="CalculatorIcon" size={20} variant="solid" />
              <span className="text-sm font-heading font-semibold">ROI Calculator</span>
            </div>
            <h2 className="text-3xl lg:text-4xl font-heading font-bold text-foreground mb-4">
              Calculate Your Support Efficiency Savings
            </h2>
            <p className="text-lg text-muted-foreground">
              See the potential cost savings and performance improvements for your organization
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            {/* Input Section */}
            <div className="space-y-6">
              <h3 className="text-xl font-heading font-bold text-foreground mb-6">Your Current Metrics</h3>
              
              <div className="space-y-2">
                <label className="block text-sm font-heading font-semibold text-foreground">
                  Monthly Support Tickets
                </label>
                <input
                  type="number"
                  value={inputs.monthlyTickets}
                  onChange={(e) => handleInputChange('monthlyTickets', Number(e.target.value))}
                  className="w-full px-4 py-3 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent text-foreground"
                  min="0"
                />
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-heading font-semibold text-foreground">
                  Average Handle Time (minutes)
                </label>
                <input
                  type="number"
                  value={inputs.avgHandleTime}
                  onChange={(e) => handleInputChange('avgHandleTime', Number(e.target.value))}
                  className="w-full px-4 py-3 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent text-foreground"
                  min="0"
                />
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-heading font-semibold text-foreground">
                  Agent Hourly Cost ($)
                </label>
                <input
                  type="number"
                  value={inputs.agentHourlyCost}
                  onChange={(e) => handleInputChange('agentHourlyCost', Number(e.target.value))}
                  className="w-full px-4 py-3 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent text-foreground"
                  min="0"
                />
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-heading font-semibold text-foreground">
                  Current Satisfaction Rate (%)
                </label>
                <input
                  type="number"
                  value={inputs.currentSatisfaction}
                  onChange={(e) => handleInputChange('currentSatisfaction', Number(e.target.value))}
                  className="w-full px-4 py-3 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent text-foreground"
                  min="0"
                  max="100"
                />
              </div>
            </div>

            {/* Results Section */}
            <div className="space-y-6">
              <h3 className="text-xl font-heading font-bold text-foreground mb-6">Projected Results</h3>
              
              <div className="bg-gradient-to-br from-accent/10 to-success/10 rounded-xl p-6 border border-accent/20">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-heading font-semibold text-foreground">Monthly Savings</span>
                  <Icon name="CurrencyDollarIcon" size={20} className="text-accent" />
                </div>
                <div className="text-3xl font-heading font-bold text-foreground">
                  ${results.monthlySavings.toLocaleString()}
                </div>
              </div>

              <div className="bg-gradient-to-br from-success/10 to-primary/10 rounded-xl p-6 border border-success/20">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-heading font-semibold text-foreground">Annual Savings</span>
                  <Icon name="ChartBarIcon" size={20} className="text-success" />
                </div>
                <div className="text-3xl font-heading font-bold text-foreground">
                  ${results.annualSavings.toLocaleString()}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="bg-background rounded-lg p-4 border border-border">
                  <div className="text-sm text-muted-foreground mb-1">Time Reduction</div>
                  <div className="text-2xl font-heading font-bold text-foreground">{results.timeReduction}%</div>
                </div>
                <div className="bg-background rounded-lg p-4 border border-border">
                  <div className="text-sm text-muted-foreground mb-1">New Satisfaction</div>
                  <div className="text-2xl font-heading font-bold text-foreground">{results.satisfactionIncrease}%</div>
                </div>
              </div>

              <div className="bg-primary/10 rounded-xl p-6 border border-primary/20">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-heading font-semibold text-foreground">First Year ROI</span>
                  <Icon name="TrophyIcon" size={20} className="text-primary" />
                </div>
                <div className="text-3xl font-heading font-bold text-primary">
                  {results.roi > 0 ? '+' : ''}{results.roi}%
                </div>
              </div>

              <button className="w-full px-6 py-4 bg-accent text-accent-foreground font-heading font-semibold rounded-lg hover:bg-accent/90 hover:shadow-elevated transition-all duration-300 flex items-center justify-center space-x-2">
                <span>Get Detailed ROI Report</span>
                <Icon name="ArrowRightIcon" size={20} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ROICalculator;